import { useEffect, useState, useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import FeatureGates, { FeatureGateEnvironment, PerimeterType } from '@atlaskit/feature-gate-js-client';
import { PRODUCTION, STAGING, DEVELOPMENT, getEnvironment } from '@atlassian/temp-shared';
import { useApplicationContext } from '@css-xp/base/common-context'; // common-context is needed otherwise style unmounted error is thrown
import { isFedrampPerimeter } from '@css-xp/ppc-utils';

const PPC_UI_KEY = 'css-xp-partner-central-ui_web';

// API Keys Source: https://bitbucket.org/atlassian/feature-flag-service/src/9424fed8fb91/scripts/sdk-key-stash/src/statsigConfig.ts
const FFS_API_KEYS: Record<FeatureGateEnvironment, Partial<Record<PerimeterType, string>>> = {
  [FeatureGateEnvironment.Production]: {
    [PerimeterType.COMMERCIAL]: '6a532d43-3d53-45f3-a024-e15aaa0f3e25',
    [PerimeterType.FEDRAMP_MODERATE]: '913f06b1-3a42-49bb-95f7-1d941489ae13',
  },
  [FeatureGateEnvironment.Staging]: {
    [PerimeterType.COMMERCIAL]: 'b59f937b-20f2-4029-bedf-b984fbb6596f',
    [PerimeterType.FEDRAMP_MODERATE]: '5f63f42d-8d37-4a77-94e0-b0e27d88eae9',
  },
  [FeatureGateEnvironment.Development]: {
    [PerimeterType.COMMERCIAL]: '92e81842-3ad0-49d8-a368-ed176c894177',
  },
};

const getAPIKey = (environment: FeatureGateEnvironment, perimeter: PerimeterType) => {
  const apiKey = FFS_API_KEYS[environment]?.[perimeter];
  if (!apiKey) {
    throw new Error(`feature-flag-service API key for ${perimeter} ${environment} has not been defined.`);
  }
  return apiKey;
};

const getFeatureGateEnvironment = () => {
  const environment = getEnvironment();
  switch (environment) {
    case PRODUCTION:
      return FeatureGateEnvironment.Production;
    case STAGING:
      return FeatureGateEnvironment.Staging;
    case DEVELOPMENT:
      return FeatureGateEnvironment.Development;
    default:
      return FeatureGateEnvironment.Staging;
  }
};

interface FeatureGatesState {
  loading: boolean;
  error: any | undefined;
}

export const useFeatureGates = (): FeatureGatesState => {
  const [clientState, setClientState] = useState<FeatureGatesState>({
    loading: true,
    error: undefined,
  });

  const { atlassianAccountId } = useApplicationContext();
  const [selectedPartnerAccount] = useLocalStorage(`${atlassianAccountId}-selectedTxa`, '');

  const initializeFeatureGates = useCallback(async (options, identifiers) => {
    try {
      setClientState({
        loading: true,
        error: undefined,
      });
      await FeatureGates.initialize(options, identifiers);
      setClientState({
        loading: false,
        error: undefined,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error initializing feature gates', error);
      setClientState({
        loading: false,
        error,
      });
    }
  }, []);

  const updateFeatureGates = useCallback(async (options, identifiers) => {
    try {
      setClientState({
        loading: true,
        error: undefined,
      });
      await FeatureGates.updateUser(options, identifiers);
      setClientState({
        loading: false,
        error: undefined,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating feature gates', error);
      setClientState({
        loading: false,
        error,
      });
    }
  }, []);

  useEffect(() => {
    if (atlassianAccountId) {
      const statsigEnvironment = getFeatureGateEnvironment();
      const perimeterType = isFedrampPerimeter() ? PerimeterType.FEDRAMP_MODERATE : PerimeterType.COMMERCIAL;
      const apiKey = getAPIKey(statsigEnvironment, perimeterType);

      const options = {
        apiKey: apiKey,
        perimeter: perimeterType,
        environment: statsigEnvironment,
        targetApp: PPC_UI_KEY,
      };
      const identifiers: { atlassianAccountId: string; transactionAccountId?: string } = {
        atlassianAccountId: atlassianAccountId,
      };
      if (selectedPartnerAccount) {
        identifiers.transactionAccountId = selectedPartnerAccount;
      }
      if (FeatureGates.initializeCompleted()) {
        updateFeatureGates(options, identifiers);
      } else {
        initializeFeatureGates(options, identifiers);
      }
    }
  }, [atlassianAccountId, initializeFeatureGates, selectedPartnerAccount, updateFeatureGates]);

  return clientState;
};
